// The single data seam for both approval pages (/approvals, /approvals/:instanceId) AND the nav
// badge counter — every consumer reads through these four hooks, never by hand-rolling a fetch or
// reaching into approvalWorkflowApi directly. All traffic goes through the hand-written
// approvalWorkflowApi client (see its own header comment for why it is deliberately NOT
// orval-generated), whose baseURL is /bff/api/v1/workflow — so every request is routed through the
// BFF, per CLAUDE.md's architecture rule.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getApprovalDetail,
  getMyApprovalDecisions,
  getOpenApprovals,
  postDecideApproval,
  type DecideApprovalRequest,
} from '../api/approvalWorkflowApi';

/**
 * Hierarchical, stable query keys rooted at the literal 'approvals' segment, so a single
 * `queryClient.invalidateQueries({ queryKey: ['approvals'] })` (TanStack Query's default
 * prefix-match invalidation) refreshes the inbox's open tab, its decided tab, the detail page,
 * AND the nav badge counter together — the detail page's approve/reject mutation calls exactly
 * this one invalidation.
 */
export const approvalsQueryKeys = {
  all: ['approvals'] as const,
  open: ['approvals', 'open'] as const,
  decisions: ['approvals', 'decisions'] as const,
  detail: (id: string | undefined) => ['approvals', 'detail', id] as const,
};

/**
 * The caller's currently open, decidable approval instances — the inbox's open tab. The nav badge
 * counter reuses this SAME hook/query key rather than a separate count query, so mounting both the
 * inbox and the nav badge issues exactly one network request and both read one shared TanStack
 * Query cache entry. `enabled` lets NavMenu mount the query only for admin/Approving callers
 * (Phase 43 D-11/D-13, RESEARCH Pitfall 8) — a role-gated server route would otherwise 403 on
 * first paint for everyone else.
 */
export function useOpenApprovals(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: approvalsQueryKeys.open,
    queryFn: ({ signal }) => getOpenApprovals(signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The caller's own past decisions, newest first — the inbox's decided tab.
 */
export function useMyApprovalDecisions() {
  return useQuery({
    queryKey: approvalsQueryKeys.decisions,
    queryFn: ({ signal }) => getMyApprovalDecisions(signal),
  });
}

/**
 * A single instance's full detail, keyed by instance id (D-24) — the detail page's primary read.
 * Disabled while `instanceId` is undefined (e.g. before the route param has resolved) so no
 * request fires.
 */
export function useApprovalDetail(instanceId: string | undefined) {
  return useQuery({
    queryKey: approvalsQueryKeys.detail(instanceId),
    queryFn: ({ signal }) => getApprovalDetail(instanceId as string, signal),
    enabled: !!instanceId,
  });
}

/**
 * The single decide mutation for both the "Genehmigen" button and the reject dialog's confirm
 * button: one hook instance, called with a `decision` of 'Approved' or 'Rejected'. There is no
 * `via` marker any more — D-01 removes the mail-link entry point (`ApprovalStageMailSender`) it
 * used to distinguish.
 *
 * On success, invalidates the whole ['approvals'] key prefix (TanStack Query's default
 * prefix-match invalidation) so the inbox's open/decided tabs, this instance's own detail query,
 * and the nav badge counter all refresh from the approver's own action in one call.
 */
export function useDecideApproval(instanceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: DecideApprovalRequest) => postDecideApproval(instanceId as string, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: approvalsQueryKeys.all });
    },
  });
}
